// src\components\StatsSection.jsx

import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  FiBriefcase,
  FiUsers,
  FiAward,
  FiGlobe,
  FiClock,
  FiHeart,
  FiStar,
  FiTrendingUp,
  FiCode,
  FiPackage,
  FiMonitor,
  FiDatabase,
  FiCloud,
  FiTool,
  FiBox,
  FiSliders,
  FiDroplet,
  FiSun,
  FiMoon,
  FiCompass,
  FiHexagon,
  FiImage,
  FiBookOpen,
  FiRefreshCw,
  FiCheckCircle,
  FiTarget,
  FiZap,
  FiEye,
  FiPenTool,
  FiSmile,
  FiThumbsUp,
} from 'react-icons/fi';
import { usePortfolio } from '../hooks/usePortfolio';

const scaleUp = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

const AnimatedCounter = ({
  target,
  duration = 2000,
  suffix = '',
  prefix = '',
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.3 });

  useEffect(() => {
    if (isInView) {
      let startTime;
      let animationFrame;
      const updateCount = timestamp => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * target));
        if (progress < 1) {
          animationFrame = requestAnimationFrame(updateCount);
        }
      };
      animationFrame = requestAnimationFrame(updateCount);
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [isInView, target, duration]);

  const formatValue = () => {
    let value = count.toLocaleString();
    if (suffix === '%') {
      value = `${value}%`;
    } else if (suffix === 'k' && count >= 1000) {
      value = `${(count / 1000).toFixed(1)}k`;
    } else if (suffix === '⭐' && count > 0) {
      value = `${count.toFixed(1)}⭐`;
    } else if (suffix) {
      value = `${value}${suffix}`;
    }
    return `${prefix}${value}`;
  };

  return <span ref={ref}>{formatValue()}</span>;
};

const StatItem = ({
  icon: Icon,
  value,
  label,
  suffix = '',
  prefix = '',
  delay = 0,
  variant = 'default',
}) => {
  if (variant === 'compact') {
    return (
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={scaleUp}
        transition={{ delay }}
        className="flex items-center gap-4 p-4 rounded-xl transition-all duration-300 hover:scale-[1.02] group"
        style={{
          backgroundColor: 'var(--bg)',
          border: '1px solid var(--border)',
        }}
      >
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110"
          style={{
            backgroundColor: 'var(--border)',
            color: 'var(--text)',
          }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xl font-bold" style={{ color: 'var(--text)' }}>
            <AnimatedCounter target={value} suffix={suffix} prefix={prefix} />
          </div>
          <div className="text-xs" style={{ color: 'var(--muted)' }}>
            {label}
          </div>
        </div>
      </motion.div>
    );
  }

  if (variant === 'minimal') {
    return (
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={scaleUp}
        transition={{ delay }}
        className="text-center p-6 rounded-xl transition-all duration-300 hover:bg-black/5 dark:hover:bg-white/5"
      >
        <div
          className="text-2xl md:text-3xl font-bold mb-1"
          style={{ color: 'var(--text)' }}
        >
          <AnimatedCounter target={value} suffix={suffix} prefix={prefix} />
        </div>
        <div className="text-sm" style={{ color: 'var(--muted)' }}>
          {label}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={scaleUp}
      transition={{ delay }}
      className="text-center group"
    >
      <div
        className="relative w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:scale-110 group-hover:-translate-y-1"
        style={{
          backgroundColor: 'var(--border)',
          border: '1px solid var(--border)',
        }}
      >
        <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <div className="absolute inset-0 rounded-2xl bg-black/5 dark:bg-white/5" />
        </div>
        <Icon
          className="w-7 h-7 transition-all duration-300 group-hover:scale-110"
          style={{ color: 'var(--text)' }}
        />
      </div>
      <div
        className="text-3xl md:text-4xl font-bold mb-1.5 tracking-tight"
        style={{ color: 'var(--text)' }}
      >
        <AnimatedCounter target={value} suffix={suffix} prefix={prefix} />
      </div>
      <div
        className="text-sm font-medium tracking-wide uppercase text-xs"
        style={{ color: 'var(--muted)' }}
      >
        {label}
      </div>
    </motion.div>
  );
};

const StatsSection = ({
  stats = [],
  columns = 4,
  variant = 'default',
  showBorder = true,
  className = '',
  title = '',
  subtitle = '',
  showTitle = false,
  animated = true,
  fetchProjectCount = false,
  projectCount = null,
}) => {
  // FIX: Use allProjects and getProjectCount from usePortfolio
  const { allProjects, loading, getProjectCount } = usePortfolio();
  const [count, setCount] = useState(0);

  // FIX: Always use getProjectCount() for reliable count
  useEffect(() => {
    if (fetchProjectCount && !loading) {
      const reliableCount = getProjectCount();
      console.log(
        `📊 [StatsSection] Project count from getProjectCount: ${reliableCount}`,
      );
      console.log(
        `📊 [StatsSection] allProjects length: ${allProjects?.length || 0}`,
      );
      setCount(reliableCount);
    }
  }, [allProjects, loading, fetchProjectCount, getProjectCount]);

  // Use manual project count if provided, otherwise use fetched count
  const finalProjectCount = projectCount !== null ? projectCount : count;

  const defaultStats = [
    {
      icon: FiBriefcase,
      value: fetchProjectCount ? finalProjectCount : 120,
      label: 'Projects Completed',
      suffix: '+',
    },
    {
      icon: FiSmile,
      value: 95,
      label: 'Happy Clients',
      suffix: '%',
    },
    {
      icon: FiClock,
      value: 5,
      label: 'Years Experience',
      suffix: '+',
    },
    {
      icon: FiGlobe,
      value: 4,
      label: 'Countries Served',
      suffix: '+',
    },
  ];

  const iconMap = {
    FiBriefcase,
    FiUsers,
    FiAward,
    FiGlobe,
    FiClock,
    FiHeart,
    FiStar,
    FiTrendingUp,
    FiCode,
    FiPackage,
    FiMonitor,
    FiDatabase,
    FiCloud,
    FiTool,
    FiBox,
    FiSliders,
    FiDroplet,
    FiSun,
    FiMoon,
    FiCompass,
    FiHexagon,
    FiImage,
    FiBookOpen,
    FiRefreshCw,
    FiCheckCircle,
    FiTarget,
    FiZap,
    FiEye,
    FiPenTool,
    FiSmile,
    FiThumbsUp,
  };

  const processedStats =
    stats.length > 0
      ? stats.map(stat => ({
          ...stat,
          icon:
            typeof stat.icon === 'string'
              ? iconMap[stat.icon] || FiBriefcase
              : stat.icon,
        }))
      : defaultStats;

  const gridCols = {
    2: 'grid-cols-2 sm:grid-cols-2',
    3: 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
    6: 'grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6',
  };

  const getColumnClass = () => {
    return gridCols[columns] || gridCols[4];
  };

  const getPadding = () => {
    if (variant === 'compact') return 'py-8 sm:py-12';
    if (variant === 'minimal') return 'py-12 sm:py-16';
    return 'py-16 sm:py-20';
  };

  if (fetchProjectCount && loading) {
    return (
      <section
        className={`${getPadding()} px-4 overflow-hidden ${className}`}
        style={{
          ...(showBorder && {
            borderTop: '1px solid var(--border)',
            borderBottom: '1px solid var(--border)',
          }),
        }}
      >
        <div className="max-w-6xl mx-auto">
          {showTitle && (title || subtitle) && (
            <div className="text-center mb-8 sm:mb-12">
              {title && (
                <h2
                  className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2"
                  style={{ color: 'var(--text)' }}
                >
                  {title}
                </h2>
              )}
              {subtitle && (
                <p
                  className="text-sm sm:text-base max-w-2xl mx-auto"
                  style={{ color: 'var(--muted)' }}
                >
                  {subtitle}
                </p>
              )}
            </div>
          )}
          <div className={`grid ${getColumnClass()} gap-4 sm:gap-6`}>
            {Array.from({ length: processedStats.length }).map((_, index) => (
              <div
                key={index}
                className="text-center p-4 sm:p-6 rounded-2xl animate-pulse"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <div
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl mx-auto mb-3 sm:mb-4"
                  style={{ backgroundColor: 'var(--bg)' }}
                />
                <div
                  className="h-6 w-16 sm:w-20 mx-auto mb-1 rounded"
                  style={{ backgroundColor: 'var(--bg)' }}
                />
                <div
                  className="h-4 w-24 sm:w-28 mx-auto rounded"
                  style={{ backgroundColor: 'var(--bg)' }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!animated) {
    return (
      <section
        className={`${getPadding()} px-4 overflow-hidden ${className}`}
        style={{
          ...(showBorder && {
            borderTop: '1px solid var(--border)',
            borderBottom: '1px solid var(--border)',
          }),
        }}
      >
        <div className="max-w-6xl mx-auto">
          {showTitle && (title || subtitle) && (
            <div className="text-center mb-8 sm:mb-12">
              {title && (
                <h2
                  className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2"
                  style={{ color: 'var(--text)' }}
                >
                  {title}
                </h2>
              )}
              {subtitle && (
                <p
                  className="text-sm sm:text-base max-w-2xl mx-auto"
                  style={{ color: 'var(--muted)' }}
                >
                  {subtitle}
                </p>
              )}
            </div>
          )}
          <div className={`grid ${getColumnClass()} gap-4 sm:gap-6`}>
            {processedStats.map((stat, index) => (
              <StatItem
                key={index}
                icon={stat.icon}
                value={stat.value}
                label={stat.label}
                suffix={stat.suffix || ''}
                prefix={stat.prefix || ''}
                delay={0}
                variant={variant}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-50px' }}
      variants={staggerContainer}
      className={`${getPadding()} px-4 overflow-hidden ${className}`}
      style={{
        ...(showBorder && {
          borderTop: '1px solid var(--border)',
          borderBottom: '1px solid var(--border)',
        }),
      }}
    >
      <div className="max-w-6xl mx-auto">
        {showTitle && (title || subtitle) && (
          <motion.div variants={fadeInUp} className="text-center mb-8 sm:mb-12">
            {title && (
              <h2
                className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2"
                style={{ color: 'var(--text)' }}
              >
                {title}
              </h2>
            )}
            {subtitle && (
              <p
                className="text-sm sm:text-base max-w-2xl mx-auto"
                style={{ color: 'var(--muted)' }}
              >
                {subtitle}
              </p>
            )}
          </motion.div>
        )}
        <div className={`grid ${getColumnClass()} gap-4 sm:gap-6 md:gap-8`}>
          {processedStats.map((stat, index) => (
            <StatItem
              key={index}
              icon={stat.icon}
              value={stat.value}
              label={stat.label}
              suffix={stat.suffix || ''}
              prefix={stat.prefix || ''}
              delay={index * 0.1}
              variant={variant}
            />
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default StatsSection;
